import React, { useRef } from 'react';
import './style.scss';
import { useActiveBanner, evictBannerCache } from '../../../Hooks/useActiveBanner';
import { ReactGA } from '../../../Utils/ga4';

const BASE_URL = process.env.baseUrl;
const NAV_TIMEOUT_MS = 1500;

const clickEndpoint = (slug) =>
    `/api/v1/campaign/track/${encodeURIComponent(slug)}/click`;

const appendUtms = (url, utms) => {
    try {
        const u = new URL(url);
        Object.keys(utms).forEach((k) => {
            if (utms[k] != null) u.searchParams.set(k, utms[k]);
        });
        return u.toString();
    } catch (e) {
        return url;
    }
};

const HomeBanner = () => {
    const { status, banner } = useActiveBanner();
    const inFlight = useRef(false);

    if (status !== 'ready' || !banner) return null;

    const handleClick = (e) => {
        e.preventDefault();
        if (inFlight.current) return;
        inFlight.current = true;

        const utms = {
            utm_source: 'lioncharge',
            utm_medium: 'home_banner',
            utm_campaign: banner.slug,
        };

        try {
            ReactGA.sendEvent({
                category: 'engagement',
                action: 'home_banner_click',
                label: banner.slug,
            });
        } catch (_) {
            // analytics is best-effort; backend also records this click
        }

        // Single-shot navigation: whoever wins the race below calls this exactly
        // once. The guard keeps any later resolver (orphaned timeout, retried
        // handler) from reassigning location mid-unload.
        let navigated = false;
        const navigateTo = (rawUrl) => {
            if (navigated) return;
            navigated = true;
            window.location.assign(appendUtms(rawUrl || banner.fallback_url, utms));
        };

        let timeoutId;
        const timeout = new Promise((resolve) => {
            timeoutId = setTimeout(
                () => resolve({ url: banner.fallback_url }),
                NAV_TIMEOUT_MS,
            );
        });

        const token = localStorage.getItem('t_id');
        const recorded = fetch(`${BASE_URL}${clickEndpoint(banner.slug)}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                Authorization: `Bearer ${token}`,
                'x-auth-token': `Bearer ${token}`,
            },
            keepalive: true,
            body: JSON.stringify({ ...utms, referrer: window.location.href }),
        })
            .then(async (res) => {
                // Campaign deactivated mid-session — drop cache so next session refetches.
                if (res.status === 404) evictBannerCache();
                if (!res.ok) return { url: banner.fallback_url };
                const json = await res.json().catch(() => null);
                const redirect =
                    json && json.payload && json.payload.redirect_url;
                return { url: redirect || banner.fallback_url };
            })
            .catch(() => ({ url: banner.fallback_url }));

        Promise.race([recorded, timeout]).then(({ url }) => {
            clearTimeout(timeoutId);
            navigateTo(url);
        });
    };

    return (
        <a
            href={banner.fallback_url}
            data-campaign-banner=""
            data-campaign-slug={banner.slug}
            onClick={handleClick}
            rel="noopener noreferrer"
            className="HomeBanner"
            aria-label={`Promotional banner: ${banner.text}. Opens partner page.`}
        >
            <span className="HomeBanner__text">{banner.text}</span>
        </a>
    );
};

export default HomeBanner;
